/**
 * App lifecycle and deep links. Native: @capacitor/app. Web: the page's own
 * visibility events stand in for resume, and there are no deep links.
 */
import { hasPlugin, isNative } from './platform'

/** Call `handler` with the path part of any klunkers:// link that opens the app. */
export async function onDeepLink(handler: (path: string) => void): Promise<void> {
  if (!isNative() || !hasPlugin('App')) return
  const { App } = await import('@capacitor/app')
  const open = (url: string) => {
    const path = deepLinkPath(url)
    if (path) handler(path)
  }
  await App.addListener('appUrlOpen', ({ url }) => open(url))
  // A cold start from a link: the event may have fired before we listened.
  const launch = await App.getLaunchUrl().catch(() => undefined)
  if (launch?.url) open(launch.url)
}

/** Run `handler` whenever the app comes back to the foreground. */
export async function onResume(handler: () => void): Promise<void> {
  if (isNative() && hasPlugin('App')) {
    const { App } = await import('@capacitor/app')
    await App.addListener('resume', handler)
    return
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') handler()
  })
}

/**
 * `klunkers://capture` → `capture`; `klunkers://jobs/abc` → `jobs/abc`.
 * Anything that is not our scheme is ignored.
 */
export function deepLinkPath(url: string): string | null {
  const match = /^klunkers:\/\/(.*)$/i.exec(url.trim())
  if (!match) return null
  return match[1]!.replace(/^\/+/, '').replace(/\/+$/, '')
}
